import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Html, Line } from '@react-three/drei';
import * as THREE from 'three';
import { annulusShape, extrudeOpts } from './wallShape.js';

const CABLE_COLOR_HEX = {
  BLUE: '#3B82F6', BROWN: '#8B4513', 'GREEN-YELLOW': '#9ACD32', GREY: '#808080',
  BLACK: '#333333', WHITE: '#EEEEEE', RED: '#EF4444', YELLOW: '#F59E0B',
  GREEN: '#22C55E', ORANGE: '#F97316'
};
const COPPER = '#B87333';
const FILL_HIGHLIGHT = '#22d3ee'; // distinctive cyan for the calculated fill area
const insulationHex = c => CABLE_COLOR_HEX[String(c || '').toUpperCase()] || '#D9D2C5';
const tag = color => ({
  color, fontSize: 10, fontFamily: 'monospace', whiteSpace: 'nowrap',
  background: 'rgba(15,20,32,0.85)', padding: '1px 4px', borderRadius: 3, pointerEvents: 'none'
});
const sheathHex = (type, productColor) =>
  CABLE_COLOR_HEX[String(productColor || '').toUpperCase()] ||
  (String(type || '').toUpperCase().includes('M1') ? '#C9C4BC' : '#6b7280');

// a filled disc of radius `outerR` with circular holes → its area = the fill material
function fillShape(outerR, holes) {
  const shape = new THREE.Shape();
  shape.absarc(0, 0, outerR, 0, Math.PI * 2, false);
  for (const h of holes) {
    const p = new THREE.Path();
    p.absarc(h.x, h.y, h.r, 0, Math.PI * 2, true);
    shape.holes.push(p);
  }
  return shape;
}

// a stadium / rounded-rectangle THREE.Shape (fully-rounded short ends) for flat cables
function stadiumShapeXY(width, height, holes = []) {
  const w = Math.max(width, height);
  const h = height;
  const r = Math.min(h / 2, w / 2);
  const s = new THREE.Shape();
  const x = -w / 2;
  const y = -h / 2;
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.absarc(x + w - r, y + r, r, -Math.PI / 2, Math.PI / 2, false);
  s.lineTo(x + r, y + h);
  s.absarc(x + r, y + r, r, Math.PI / 2, Math.PI * 1.5, false);
  s.closePath();
  for (const hole of holes) {
    const p = new THREE.Path();
    p.absarc(hole.x, hole.y, hole.r, 0, Math.PI * 2, true);
    s.holes.push(p);
  }
  return s;
}

const SheathLayer3D = ({
  layer, productColor, height = 440,
  bedding = false, cores = [], numberOfConductors = 0, ropeDiameter = 0, interstitial = false,
  flat = false
}) => {
  const innerR = layer ? layer.innerD / 2 : 0;
  const outerR = layer ? layer.outerD / 2 : 0;

  // conductor centres on the pitch circle + the two fill shapes (OFF ring / ON full)
  const geo = useMemo(() => {
    if (!layer || !bedding || !cores.length) return null;
    // actual core count (reduced-GY cable has one extra core beyond numberOfConductors)
    const n = Math.max(cores.length, numberOfConductors || 1);
    const coreR = Math.max(...cores.map(c => c.outerD / 2));
    const ropeR = ropeDiameter / 2;
    const pitchR = n <= 1 ? 0 : Math.max(ropeR - coreR, coreR / Math.sin(Math.PI / n));
    const centres = n <= 1 ? [[0, 0]] : Array.from({ length: n }, (_, i) => {
      const a = (i / n) * Math.PI * 2 - Math.PI / 2;
      return [Math.cos(a) * pitchR, Math.sin(a) * pitchR];
    });
    const condHoles = centres.map((c, i) => ({ x: c[0], y: c[1], r: (cores[Math.min(i, cores.length - 1)].outerD) / 2 }));
    return {
      centres, coreR, ropeR,
      offShape: fillShape(outerR, [{ x: 0, y: 0, r: ropeR }]),   // standard: only the outer ring
      onShape: fillShape(outerR, condHoles),                      // real: everything but the conductors
      condHoles
    };
  }, [layer, bedding, cores, numberOfConductors, ropeDiameter, outerR]);

  const sides = layer?.wall?.sides || { top: 0, right: 0, bottom: 0, left: 0 };
  const shape = useMemo(
    () => (layer && !bedding && !flat ? annulusShape(innerR, sides, 96) : null),
    [layer, bedding, flat, innerR, sides.top, sides.right, sides.bottom, sides.left]
  );

  // flat cable (MYYUP): rectangular (stadium) sheath with conductors laid in a row
  const flatGeo = useMemo(() => {
    if (!layer || !flat || !cores.length) return null;
    const n = numberOfConductors || cores.length;
    const coreD = Math.max(...cores.map(c => c.outerD));
    const wall = Number(layer.wall?.avgSpec) > 0
      ? layer.wall.avgSpec
      : Math.max(0.2, (layer.outerD - layer.innerD) / 2);
    const width = n * coreD + 2 * wall;
    const heightRect = coreD + 2 * wall;
    const centres = Array.from({ length: n }, (_, i) => [(i - (n - 1) / 2) * coreD, 0]);
    const holes = centres.map((c, i) => ({ x: c[0], y: c[1], r: cores[Math.min(i, cores.length - 1)].outerD / 2 }));
    return { width, heightRect, centres, shape: stadiumShapeXY(width, heightRect, holes), wall };
  }, [layer, flat, cores, numberOfConductors]);

  if (!layer) {
    return <div style={{ height, background: '#0f1420', borderRadius: 6 }}
      className="d-flex align-items-center justify-content-center text-500">No layer</div>;
  }
  const len = outerR * 3;
  const capZ = len / 2;
  const cam = flat && flatGeo ? flatGeo.width * 2.4 : outerR * 6;
  const color = sheathHex(layer.type, layer.name === 'exterior_sheat' ? productColor : null);
  const depth = Math.max(0.2, outerR * 0.4);
  const fillColor = interstitial ? FILL_HIGHLIGHT : color;

  return (
    <div style={{ height, background: '#0f1420', borderRadius: 6, overflow: 'hidden' }}>
      <Canvas camera={{ position: [cam * 0.35, cam * 0.35, cam], fov: 40 }} dpr={[1, 2]}>
        <color attach="background" args={['#0f1420']} />
        <ambientLight intensity={0.95} />
        <directionalLight position={[outerR * 4, outerR * 6, outerR * 8]} intensity={1.05} />
        <directionalLight position={[-outerR * 4, 0, outerR * 4]} intensity={0.4} />
        <OrbitControls enablePan enableZoom />

        {flat && flatGeo ? (
          <group>
            {/* rectangular (stadium) sheath body with conductor holes */}
            <mesh position={[0, 0, -depth / 2]}>
              <extrudeGeometry args={[flatGeo.shape, { depth, bevelEnabled: false, curveSegments: 48 }]} />
              <meshStandardMaterial color={color} transparent opacity={0.6} roughness={0.6} side={2} />
            </mesh>
            {/* solid front cap */}
            <mesh position={[0, 0, depth / 2]}>
              <extrudeGeometry args={[flatGeo.shape, { depth: 0.02, bevelEnabled: false, curveSegments: 48 }]} />
              <meshStandardMaterial color={color} roughness={0.6} side={2} />
            </mesh>
            {/* conductors laid side by side */}
            {flatGeo.centres.map((c, i) => {
              const core = cores[Math.min(i, cores.length - 1)];
              const cR = core.copperD / 2, oR = core.outerD / 2;
              return (
                <group key={i} position={[c[0], c[1], depth / 2 + 0.03]}>
                  <mesh><circleGeometry args={[oR, 40]} /><meshStandardMaterial color={insulationHex(core.color)} roughness={0.7} /></mesh>
                  <mesh position={[0, 0, 0.01]}><circleGeometry args={[cR, 32]} /><meshStandardMaterial color={COPPER} metalness={0.85} roughness={0.35} /></mesh>
                </group>
              );
            })}
            {/* outer edge outline */}
            <Line points={flatGeo.shape.getPoints(120).map(p => [p.x, p.y, depth / 2 + 0.04])} color="#9fb3c8" lineWidth={1.5} />
          </group>
        ) : bedding && geo ? (
          <group>
            {/* the FILL material (what's counted): OFF = ring only, ON = full area */}
            <mesh position={[0, 0, -depth / 2]}>
              <extrudeGeometry args={[interstitial ? geo.onShape : geo.offShape, { depth, bevelEnabled: false, curveSegments: 48 }]} />
              <meshStandardMaterial color={fillColor} transparent opacity={interstitial ? 0.85 : 0.65}
                emissive={interstitial ? FILL_HIGHLIGHT : '#000'} emissiveIntensity={interstitial ? 0.25 : 0} roughness={0.6} side={2} />
            </mesh>
            {/* the conductors (rope) — copper core + insulation, sitting inside */}
            {geo.centres.map((c, i) => {
              const core = cores[Math.min(i, cores.length - 1)];
              const cR = core.copperD / 2, oR = core.outerD / 2;
              return (
                <group key={i} position={[c[0], c[1], depth / 2 + 0.02]}>
                  <mesh><ringGeometry args={[cR, oR, 40]} /><meshStandardMaterial color={insulationHex(core.color)} side={2} roughness={0.7} /></mesh>
                  <mesh position={[0, 0, 0.01]}><circleGeometry args={[cR, 32]} /><meshStandardMaterial color={COPPER} metalness={0.85} roughness={0.35} /></mesh>
                </group>
              );
            })}
            {/* sheath outer edge outline */}
            <Line points={Array.from({ length: 65 }, (_, i) => { const a = i / 64 * Math.PI * 2; return [Math.cos(a) * outerR, Math.sin(a) * outerR, depth / 2 + 0.03]; })} color="#9fb3c8" lineWidth={1.5} />
          </group>
        ) : (
          <>
            {/* non-bedding sheath: variable-wall tube over a grey core, 4-side callouts */}
            <mesh rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[innerR, innerR, len * 1.02, 48]} /><meshStandardMaterial color="#556" roughness={0.85} />
            </mesh>
            {shape && (
              <>
                <mesh position={[0, 0, -len / 2]}>
                  <extrudeGeometry args={[shape, extrudeOpts(len)]} />
                  <meshStandardMaterial color={color} transparent opacity={0.55} roughness={0.6} side={2} />
                </mesh>
                <mesh position={[0, 0, capZ]}>
                  <extrudeGeometry args={[shape, extrudeOpts(0.02)]} />
                  <meshStandardMaterial color={color} side={2} roughness={0.6} />
                </mesh>
              </>
            )}
            <group position={[0, 0, capZ + 0.03]}>
              {[
                { p1: [0, innerR], p2: [0, innerR + sides.top], lp: [0, innerR + sides.top + 0.2], v: sides.top, k: 'top' },
                { p1: [innerR, 0], p2: [innerR + sides.right, 0], lp: [innerR + sides.right + 0.45, 0], v: sides.right, k: 'right' },
                { p1: [0, -innerR], p2: [0, -(innerR + sides.bottom)], lp: [0, -(innerR + sides.bottom) - 0.2], v: sides.bottom, k: 'bottom' },
                { p1: [-innerR, 0], p2: [-(innerR + sides.left), 0], lp: [-(innerR + sides.left) - 0.45, 0], v: sides.left, k: 'left' }
              ].map(rl => (
                <group key={rl.k}>
                  <Line points={[[...rl.p1, 0], [...rl.p2, 0]]} color="#ffd166" lineWidth={2} />
                  <Html position={[...rl.lp, 0]} center><div style={tag('#ffd166')}>{rl.k} {rl.v.toFixed(2)}</div></Html>
                </group>
              ))}
            </group>
          </>
        )}

        <Html position={[0, flat && flatGeo ? -(flatGeo.heightRect * 1.2) : -outerR * 1.7, 0]} center>
          <div style={{ color: '#9fb3c8', fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
            {layer.name.replace('_', ' ')}
            {flat && flatGeo
              ? ` · flat ${flatGeo.width.toFixed(2)} × ${flatGeo.heightRect.toFixed(2)} mm`
              : ` · Ø ${layer.outerD.toFixed(2)} mm`}
            {bedding && !flat && (interstitial
              ? <span style={{ color: FILL_HIGHLIGHT }}> · fill = gaps + ring (counted)</span>
              : <span> · standard fills only the outer ring</span>)}
          </div>
        </Html>
      </Canvas>
    </div>
  );
};

export default SheathLayer3D;
